import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

const Container = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: black;
  user-select: none;
`;

const CameraFeed = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(1);
  display: ${props => props.$show ? 'block' : 'none'};
  z-index: 1;
`;

const VirtualBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: ${props => props.$show ? 'block' : 'none'};
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.03) 0px,
        rgba(255, 255, 255, 0.03) 1px,
        transparent 1px,
        transparent 40px
      ),
      repeating-linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.03) 0px,
        rgba(255, 255, 255, 0.03) 1px,
        transparent 1px,
        transparent 40px
      );
  }
`;

const ThreeCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto; /* 항상 클릭 가능하도록 */
  cursor: ${props => props.$interactive ? 'grab' : 'pointer'};
  touch-action: none;
  z-index: 2; /* 배경 위에 표시 */

  &:active {
    cursor: ${props => props.$interactive ? 'grabbing' : 'pointer'};
  }
`;

const ShowModelsButton = styled.button`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  padding: 16px 40px;
  font-size: 18px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
  z-index: 20; /* 캔버스보다 위에 */
  display: ${props => props.$hide ? 'none' : 'block'};

  &:hover {
    transform: translateX(-50%) translateY(-2px);
    box-shadow: 0 8px 30px rgba(99, 102, 241, 0.6);
  }

  &:active {
    transform: translateX(-50%) translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 16px;
    bottom: 30px;
  }

  @media (max-width: 480px) {
    padding: 10px 24px;
    font-size: 14px;
    bottom: 20px;
  }

  @media (max-width: 350px) {
    padding: 8px 20px;
    font-size: 12px;
    bottom: 15px;
  }
`;

const SliderContainer = styled.div`
  position: absolute;
  bottom: ${props => props.$isVisible ? '20px' : '-300px'};
  left: 0;
  right: 0;
  height: 200px;
  transition: bottom 0.5s ease;
  z-index: 30; /* 모든 요소보다 위에 */

  @media (max-width: 768px) {
    height: 180px;
    bottom: ${props => props.$isVisible ? '15px' : '-280px'};
  }

  @media (max-width: 480px) {
    height: 150px;
    bottom: ${props => props.$isVisible ? '10px' : '-250px'};
  }

  @media (max-width: 350px) {
    height: 130px;
    bottom: ${props => props.$isVisible ? '5px' : '-230px'};
  }
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 0 60px;

  @media (max-width: 768px) {
    gap: 15px;
    padding: 0 50px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    padding: 0 40px;
  }

  @media (max-width: 350px) {
    gap: 8px;
    padding: 0 30px;
  }
`;

const SliderTrack = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 10px;
  flex: 1;
  justify-content: center;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 768px) {
    gap: 15px;
    padding: 8px;
    justify-content: flex-start;
  }

  @media (max-width: 480px) {
    gap: 10px;
    padding: 5px;
  }

  @media (max-width: 350px) {
    gap: 8px;
    padding: 3px;
  }
`;

const SliderCard = styled.div`
  min-width: 150px;
  height: 150px;
  background: white;
  border-radius: 16px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid ${props => props.$selected ? '#6366f1' : 'transparent'};
  box-shadow: ${props => props.$selected ? '0 0 20px rgba(99, 102, 241, 0.5)' : '0 4px 10px rgba(0, 0, 0, 0.2)'};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (max-width: 768px) {
    min-width: 130px;
    height: 130px;
    padding: 8px;
    gap: 6px;
  }

  @media (max-width: 480px) {
    min-width: 100px;
    height: 100px;
    padding: 5px;
    gap: 4px;
    border-radius: 12px;
  }

  @media (max-width: 350px) {
    min-width: 85px;
    height: 85px;
    padding: 4px;
    gap: 2px;
    border-radius: 10px;
  }
`;

const CardImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 10px;

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    border-radius: 8px;
  }

  @media (max-width: 350px) {
    width: 50px;
    height: 50px;
    border-radius: 6px;
  }
`;

const CardName = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
  width: 100%;
  padding: 0 2px;

  @media (max-width: 768px) {
    font-size: 11px;
    max-width: 120px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    max-width: 88px;
  }

  @media (max-width: 350px) {
    font-size: 9px;
    max-width: 75px;
  }
`;

const NavButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }

  @media (max-width: 350px) {
    width: 28px;
    height: 28px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 20;

  @media (max-width: 480px) {
    gap: 15px;
  }

  @media (max-width: 350px) {
    gap: 10px;
  }
`;

const LoadingText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }

  @media (max-width: 350px) {
    font-size: 12px;
  }
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 250px;
  }

  @media (max-width: 480px) {
    width: 200px;
    height: 6px;
  }

  @media (max-width: 350px) {
    width: 150px;
    height: 5px;
  }
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #6366f1 0%, #a855f7 100%);
  border-radius: 10px;
  transition: width 0.3s ease;
  width: ${props => props.$progress}%;
`;

const ProgressText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }

  @media (max-width: 350px) {
    font-size: 10px;
  }
`;

// 랜덤 배열 셔플 함수
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export default function ARLandingPage() {
  const navigate = useNavigate();
  const [cameraReady, setCameraReady] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);
  const [showSlider, setShowSlider] = useState(false);
  const [arObjects, setArObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modelLoading, setModelLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [modelVisible, setModelVisible] = useState(false);
  const [modelScale, setModelScale] = useState(1);
  const [modelRotation, setModelRotation] = useState({ x: 0, y: 0 });
  const [loadError, setLoadError] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const sliderRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const modelRef = useRef(null);
  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });

  // 카메라 초기화
  useEffect(() => {
    const initCamera = async () => {
      try {
        // 먼저 user 카메라로 시도, 실패하면 environment 카메라 시도
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'environment',
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            }
          });
        } catch (envError) {
          console.log('후면 카메라 없음, 전면 카메라 시도...');
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'user',
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            }
          });
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          setHasCamera(true);
          setCameraReady(true);
        }
      } catch (error) {
        console.log('카메라 없음, 가상 배경 사용');
        setHasCamera(false);
        setCameraReady(true);
      }
    };

    initCamera();

    return () => {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // AR 객체 데이터 로드 (전체 데이터베이스에서 랜덤 5개)
  useEffect(() => {
    const fetchARObjects = async () => {
      try {
        setLoading(true);

        // 공개 퀴즈 가져오기
        let response = null;
        try {
          response = await fetch('/auth/get-public-quizzes', {
            credentials: 'include'
          });
        } catch (err) {
          console.log('공개 퀴즈 API 호출 실패');
        }

        if (!response || !response.ok) {
          console.log('공개 퀴즈를 불러올 수 없습니다');
          setArObjects([]);
          setLoading(false);
          return;
        }

        // JSON 파싱
        let data;
        try {
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            console.log('응답이 JSON 형식이 아닙니다');
            setArObjects([]);
            setLoading(false);
            return;
          }
          data = await response.json();
        } catch (jsonErr) {
          console.log('JSON 파싱 실패');
          setArObjects([]);
          setLoading(false);
          return;
        }

        const quizzes = data.quizzes || [];

        if (quizzes.length === 0) {
          console.log('공개 퀴즈가 없습니다');
          setArObjects([]);
          setLoading(false);
          return;
        }

        console.log('📋 공개 퀴즈 데이터:', quizzes);

        // 모든 공개 퀴즈에서 AR 객체 추출
        const objectsMap = new Map();

        for (const quiz of quizzes) {
          // 퀴즈 ID 찾기 (다양한 필드명 시도)
          const quizId = quiz.question_id || quiz.set_id || quiz.id;

          if (!quizId) {
            console.log('⚠️ 퀴즈 ID 없음, 스킵:', quiz);
            continue;
          }

          console.log('🔍 퀴즈 상세 로드 시도:', quizId);

          let detailResponse = null;
          try {
            detailResponse = await fetch(`/auth/get-quiz-detail/${quizId}`, {
              credentials: 'include'
            });
          } catch (err) {
            console.log('❌ 퀴즈 상세 로드 실패:', quizId);
            continue;
          }

          if (detailResponse && detailResponse.ok) {
            try {
              const contentType = detailResponse.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const detailData = await detailResponse.json();
                if (detailData.success && detailData.quiz?.problems) {
                  detailData.quiz.problems.forEach(problem => {
                    const objectId = problem.ar_object_id;
                    const modelUrl = problem.model_url;

                    // 3D 모델이 있는 객체만 추가
                    if (objectId && modelUrl && !objectsMap.has(objectId)) {
                      objectsMap.set(objectId, {
                        id: objectId,
                        name: problem.ar_object_name || '알 수 없는 객체',
                        image: problem.ar_object_image || '/ThumbnailEx1.jpg',
                        modelUrl: modelUrl
                      });
                      console.log(`✅ 3D 모델 있는 객체 추가: ${problem.ar_object_name} - ${modelUrl}`);
                    } else if (objectId && !modelUrl) {
                      console.log(`⏭️ 3D 모델 없음, 스킵: ${problem.ar_object_name}`);
                    }
                  });
                }
              }
            } catch (jsonErr) {
              // JSON 파싱 실패 시 무시하고 계속
            }
          }
        }

        const allObjects = Array.from(objectsMap.values());

        if (allObjects.length === 0) {
          console.log('AR 객체를 찾을 수 없습니다');
          setArObjects([]);
          setLoading(false);
          return;
        }

        // 랜덤 5개 선택 (5개 미만이면 전부 표시)
        const randomObjects = shuffleArray(allObjects).slice(0, 5);
        console.log(`✅ 공개 퀴즈에서 ${randomObjects.length}개 AR 객체 로드 완료:`, randomObjects);
        setArObjects(randomObjects);
      } catch (error) {
        console.log('AR 객체 로드 중 오류');
        setArObjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchARObjects();
  }, []);

  // Three.js 초기화
  useEffect(() => {
    if (!canvasRef.current) {
      console.log('⏳ 캔버스 준비 대기 중...');
      return;
    }

    if (loading) {
      console.log('⏳ AR 객체 데이터 로딩 중...');
      return;
    }

    console.log('🎬 Three.js 초기화 시작');

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 3;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);

    // 조명 추가
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;

    console.log('✅ Three.js 초기화 완료');

    // 애니메이션 루프 (자동 회전 제거 - 수동 조작만 허용)
    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    // 리사이즈 핸들러
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
    };
  }, [loading]);

  // 선택된 객체의 3D 모델 로드
  useEffect(() => {
    console.log('🔍 모델 로드 useEffect 실행:', {
      selectedObject: selectedObject?.name,
      modelUrl: selectedObject?.modelUrl,
      sceneReady: !!sceneRef.current
    });

    if (!selectedObject?.modelUrl) {
      console.log('⚠️ modelUrl 없음');
      return;
    }

    if (!sceneRef.current) {
      console.log('⚠️ Three.js 씬이 준비되지 않음');
      return;
    }

    // 기존 모델 제거
    if (modelRef.current) {
      sceneRef.current.remove(modelRef.current);
      modelRef.current = null;
    }

    setModelVisible(false);
    setModelScale(1);
    setModelRotation({ x: 0, y: 0 });

    console.log('🔄 3D 모델 로드 시작:', selectedObject.modelUrl);
    setModelLoading(true);
    setLoadProgress(0);
    setLoadError(null);

    const loader = new GLTFLoader();

    // CORS 우회를 위한 프록시 사용 (ARGameScreen과 동일)
    const proxyUrl = `/api/proxy/glb?url=${encodeURIComponent(selectedObject.modelUrl)}`;
    console.log('📦 원본 GLB URL:', selectedObject.modelUrl);
    console.log('🔄 프록시 URL:', proxyUrl);

    loader.load(
      proxyUrl,
      (gltf) => {
        const model = gltf.scene;

        console.log('📦 원본 모델 정보:', {
          children: model.children.length,
          position: model.position,
          scale: model.scale
        });

        // 모델 크기 조정
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        console.log('📏 모델 크기:', {
          width: size.x,
          height: size.y,
          depth: size.z,
          maxDim: maxDim
        });

        // 적절한 스케일 계산 (화면의 1/3 크기로)
        const targetSize = 1.5;
        const scale = targetSize / maxDim;
        model.scale.set(scale, scale, scale);

        // 모델을 원점(0,0,0)에 배치
        const center = box.getCenter(new THREE.Vector3());
        model.position.set(
          -center.x * scale,
          -center.y * scale,
          -center.z * scale
        );

        console.log('✨ 조정된 모델:', {
          scale: scale,
          position: model.position,
          center: center
        });

        // 처음에는 숨김
        model.visible = false;

        sceneRef.current.add(model);
        modelRef.current = model;
        model.userData.baseScale = scale;

        setModelLoading(false);
        setLoadProgress(100);
        setModelVisible(true); // 로딩 완료 즉시 모델 표시

        console.log('✅ 3D 모델 씬에 추가 완료 및 표시');
      },
      (progress) => {
        if (progress.lengthComputable) {
          const percentComplete = (progress.loaded / progress.total) * 100;
          setLoadProgress(percentComplete);
          console.log(`📥 ${selectedObject.name} 로딩: ${percentComplete.toFixed(2)}%`);
        } else {
          // lengthComputable이 false인 경우 대략적인 진행률 표시
          const loaded = progress.loaded;
          const estimated = Math.min((loaded / 1000000) * 50, 90); // 1MB당 50%, 최대 90%
          setLoadProgress(estimated);
          console.log(`📥 ${selectedObject.name} 로딩 중... (${(loaded / 1024).toFixed(0)} KB)`);
        }
      },
      (error) => {
        console.error('❌ 3D 모델 로드 실패:', selectedObject.name);
        console.error('   URL:', selectedObject.modelUrl);
        console.error('   Error:', error.message);

        setModelLoading(false);
        setLoadError(error.message);

        // CORS 에러인 경우 안내 메시지
        if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
          console.error('   💡 CORS 정책으로 인해 외부 서버의 모델을 로드할 수 없습니다.');
          console.error('   💡 해결 방법: 서버에서 Access-Control-Allow-Origin 헤더 설정 필요');
        }
      }
    );
  }, [selectedObject]);

  // 모델 표시 상태 및 스케일/회전 업데이트
  useEffect(() => {
    if (modelRef.current) {
      // 모델 표시/숨김
      modelRef.current.visible = modelVisible;

      if (modelVisible) {
        // 스케일 적용
        const baseScale = modelRef.current.userData.baseScale || 1;
        modelRef.current.scale.set(
          baseScale * modelScale,
          baseScale * modelScale,
          baseScale * modelScale
        );
        // 회전 적용
        modelRef.current.rotation.x = modelRotation.x;
        modelRef.current.rotation.y = modelRotation.y;

        console.log('🎯 모델 표시됨:', {
          visible: true,
          scale: modelScale,
          rotation: modelRotation
        });
      } else {
        console.log('👻 모델 숨김');
      }
    }
  }, [modelScale, modelRotation, modelVisible]);

  const handleShowModels = () => {
    setShowSlider(true);
  };

  const handleObjectSelect = (obj) => {
    setSelectedObject(obj);
    // 슬라이더는 숨기지 않음 - 다른 객체도 선택할 수 있도록
    setModelVisible(false); // 새 모델 선택 시 일단 숨김
  };

  const handleScroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 200;
      sliderRef.current.scrollLeft += direction === 'left' ? -scrollAmount : scrollAmount;
    }
  };

  const handleCanvasClick = () => {
    if (selectedObject && modelRef.current && !modelVisible) {
      console.log('🖱️ 배경 클릭 - 모델 표시');
      setModelVisible(true);
    } else if (!selectedObject) {
      console.log('⚠️ 먼저 AR 객체를 선택하세요');
    } else if (!modelRef.current) {
      console.log('⏳ 3D 모델 로딩 중...');
    }
  };

  const handleMouseDown = (e) => {
    if (!modelVisible) return;
    isDraggingRef.current = true;
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current || !modelVisible) return;

    const deltaX = e.clientX - previousMouseRef.current.x;
    const deltaY = e.clientY - previousMouseRef.current.y;

    setModelRotation(prev => ({
      x: prev.x + deltaY * 0.01,
      y: prev.y + deltaX * 0.01
    }));

    previousMouseRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  const handleWheel = (e) => {
    if (!modelVisible) return;
    e.preventDefault();

    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setModelScale(prev => Math.max(0.5, Math.min(3, prev + delta)));
  };

  const handleTouchStart = (e) => {
    if (!modelVisible || e.touches.length !== 1) return;
    isDraggingRef.current = true;
    previousMouseRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  const handleTouchMove = (e) => {
    if (!isDraggingRef.current || !modelVisible || e.touches.length !== 1) return;
    e.preventDefault();

    const deltaX = e.touches[0].clientX - previousMouseRef.current.x;
    const deltaY = e.touches[0].clientY - previousMouseRef.current.y;

    setModelRotation(prev => ({
      x: prev.x + deltaY * 0.01,
      y: prev.y + deltaX * 0.01
    }));

    previousMouseRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  const handleTouchEnd = () => {
    isDraggingRef.current = false;
  };

  if (loading) {
    return (
      <Container>
        <VirtualBackground $show={true} />
        <LoadingText>AR 객체 데이터 로딩 중...</LoadingText>
      </Container>
    );
  }

  return (
    <Container>
      <CameraFeed ref={videoRef} autoPlay playsInline muted $show={hasCamera} />
      <VirtualBackground $show={!hasCamera} />

      <ThreeCanvas
        ref={canvasRef}
        $interactive={modelVisible}
        onClick={handleCanvasClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />

      {modelLoading && (
        <LoadingOverlay>
          <LoadingText>⏳ 3D 모델 로딩 중...</LoadingText>
          <ProgressBarContainer>
            <ProgressBarFill $progress={loadProgress} />
          </ProgressBarContainer>
          <ProgressText>{loadProgress.toFixed(0)}%</ProgressText>
        </LoadingOverlay>
      )}

      <ShowModelsButton
        onClick={showSlider ? () => setShowSlider(false) : handleShowModels}
        $hide={showSlider}
      >
        {arObjects.length > 0 ? 'AR 객체 보기' : 'AR 객체가 없습니다'}
      </ShowModelsButton>

      {arObjects.length > 0 && (
        <SliderContainer $isVisible={showSlider}>
          <SliderWrapper>
            <NavButton onClick={() => handleScroll('left')}>
              <ChevronLeft size={24} />
            </NavButton>

            <SliderTrack ref={sliderRef}>
              {arObjects.map((obj) => (
                <SliderCard
                  key={obj.id}
                  $selected={selectedObject?.id === obj.id}
                  onClick={() => handleObjectSelect(obj)}
                >
                  <CardImage src={obj.image} alt={obj.name} />
                  <CardName>{obj.name}</CardName>
                </SliderCard>
              ))}
            </SliderTrack>

            <NavButton onClick={() => handleScroll('right')}>
              <ChevronRight size={24} />
            </NavButton>
          </SliderWrapper>
        </SliderContainer>
      )}
    </Container>
  );
}
